import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';

/**
 * Lightweight i18n (no external dependency). Dictionaries are split per area
 * under ./locales (each file exports { pt, es, en, it }) and merged here. A
 * t() helper + LanguageProvider/useT() hook persist the choice in localStorage.
 * Missing keys fall back to Portuguese, then to the key itself — so a partially
 * translated locale never breaks the UI.
 */
import core from './locales/core';
import auth from './locales/auth';
import dashboard from './locales/dashboard';
import pix from './locales/pix';
import investments from './locales/investments';
import cards from './locales/cards';
import profile from './locales/profile';
import extract from './locales/extract';
import chatbot from './locales/chatbot';
import common from './locales/common';
import evildog from './locales/evildog';

// Idiomas suportados. A ordem define a exibição no seletor.
export const LANGS = [
  { code: 'pt', label: 'PT', name: 'Português', flag: '🇧🇷' },
  { code: 'es', label: 'ES', name: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'EN', name: 'English', flag: '🇺🇸' },
  { code: 'it', label: 'IT', name: 'Italiano', flag: '🇮🇹' },
];

const AREAS = [core, auth, dashboard, pix, investments, cards, profile, extract, chatbot, common, evildog];

// Junta todos os dicionários de área num único mapa por idioma.
const DICT = LANGS.reduce((acc, { code }) => {
  acc[code] = AREAS.reduce((m, area) => Object.assign(m, area[code] || {}), {});
  return acc;
}, {});

const LangContext = createContext({ lang: 'pt', setLang: () => {}, t: (k) => k });

const readLang = () => {
  try {
    const saved = localStorage.getItem('dogbank_lang');
    return LANGS.some((l) => l.code === saved) ? saved : 'pt';
  } catch { return 'pt'; }
};

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readLang);
  const setLang = (l) => {
    try { localStorage.setItem('dogbank_lang', l); } catch { /* ignore */ }
    setLangState(l);
  };
  const t = (key, vars) => {
    let s = (DICT[lang] && DICT[lang][key]) ?? (DICT.pt && DICT.pt[key]) ?? key;
    if (vars) Object.entries(vars).forEach(([k, v]) => { s = s.split(`{${k}}`).join(v); });
    return s;
  };
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export const useT = () => useContext(LangContext);

/**
 * Seletor de idioma (dropdown com os 4 idiomas). `variant` controla o tema:
 * 'light' (header do app) ou 'dark' (console EvilDog, inclusive em fullscreen).
 */
export function LanguageSelect({ variant = 'light', className = '' }) {
  const { lang, setLang } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const dark = variant === 'dark';
  const btnCls = dark
    ? 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#26313f] bg-[#0f151d] text-xs font-semibold text-slate-300 hover:text-green-400 hover:border-green-500/40'
    : 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold border border-slate-200 bg-white/70 text-slate-600 hover:text-purple-600 hover:border-purple-200 transition-colors';
  const menuCls = dark
    ? 'absolute right-0 mt-2 w-40 rounded-lg border border-[#26313f] bg-[#0f151d] shadow-xl overflow-hidden z-50'
    : 'absolute right-0 mt-2 w-40 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden z-50';
  const itemCls = (active) => dark
    ? `flex items-center justify-between gap-2 w-full px-3 py-2 text-xs font-medium text-left ${active ? 'text-green-400 bg-[#141c26]' : 'text-slate-300 hover:bg-[#141c26]'}`
    : `flex items-center justify-between gap-2 w-full px-3 py-2 text-xs font-medium text-left ${active ? 'text-purple-600 bg-purple-50' : 'text-slate-600 hover:bg-slate-50'}`;

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button onClick={() => setOpen((o) => !o)} className={btnCls} title={current.name} aria-label="Idioma / Language">
        <Globe className="w-3.5 h-3.5" />
        {current.label}
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>
      {open && (
        <div className={menuCls}>
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={itemCls(l.code === lang)}
            >
              <span className="flex items-center gap-2">
                <span>{l.flag}</span>{l.name}
              </span>
              {l.code === lang && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Compatibilidade: o antigo LanguageToggle (claro) agora é o seletor de 4 idiomas.
export function LanguageToggle({ className = '' }) {
  return <LanguageSelect variant="light" className={className} />;
}
